import type { DetectedHold } from '../../../types/climb-scan';
import { BOX_GRID, MIN_SCORE_MAX, MIN_SCORE_STEP } from '../../../gym-admin/components/scans/detected-holds';

/**
 * Which of a scanned wall's holds the climb editor offers as placeholders.
 *
 * Two lines, both hiding and never deleting — the wall keeps every hold, and a
 * hold already taken into the climb stays in it:
 *
 * - **confidence** (`minScore`): the detections scored under it, as the gym
 *   panel's slider hides them (`detected-holds.ts#isHidden`) and with the same
 *   value — saved on the scan where the reader may (see `PhotoClimbEditor`);
 * - **size** (`minSize`): the ones smaller than it, as a share of the photo.
 *   The editor's own, and nobody else's: a speck of chalk and a foot chip are
 *   both small, and which of them this climb wants is this climb's question.
 *
 * Null is no line at all.
 */
export interface HoldFilter {
  minScore: number | null;
  minSize: number | null;
}

export const NO_FILTER: HoldFilter = { minScore: null, minSize: null };

/** The confidence slider's steps and ceiling — the panel's own. */
export { MIN_SCORE_MAX, MIN_SCORE_STEP };

/** How finely the size slider moves along its (logarithmic) range. */
export const SIZE_STEPS = 100;

/**
 * How much of the photo a detection covers, 0–1: the area inside its outline,
 * which is on a 0-1000 grid in each direction.
 */
export function detectionArea(detection: DetectedHold): number {
  const points = detection.outline;
  if (points.length < 3) return 0;

  let twice = 0;
  for (let index = 0; index < points.length; index += 1) {
    const [x1, y1] = points[index];
    const [x2, y2] = points[(index + 1) % points.length];
    twice += x1 * y2 - x2 * y1;
  }
  return Math.abs(twice) / 2 / (BOX_GRID * BOX_GRID);
}

/** Whether the filter leaves this detection on the wall. */
export function passesFilter(detection: DetectedHold, filter: HoldFilter): boolean {
  // A detection with no score (Gemini) is never hidden by confidence.
  if (filter.minScore != null && typeof detection.score === 'number' && detection.score < filter.minScore) {
    return false;
  }
  return filter.minSize == null || detectionArea(detection) >= filter.minSize;
}

/** The smallest and the largest hold on the wall, or null for an empty one. */
export function sizeRange(detections: DetectedHold[]): { min: number; max: number } | null {
  const areas = detections.map(detectionArea).filter((area) => area > 0);
  if (areas.length === 0) return null;

  return { min: Math.min(...areas), max: Math.max(...areas) };
}

/**
 * The size slider's position (0…SIZE_STEPS) as a minimum size, and back.
 *
 * **On a logarithmic scale** between the wall's smallest hold and its largest:
 * a volume can be a thousand times a foot chip, and on a straight scale every
 * hold anybody would want to hide would sit in the first notch. Position 0 is
 * no line at all.
 */
export function sizeAt(step: number, range: { min: number; max: number }): number | null {
  if (step <= 0) return null;

  return range.min * (range.max / range.min) ** (step / SIZE_STEPS);
}

export function stepOf(minSize: number | null, range: { min: number; max: number }): number {
  if (minSize == null || range.max <= range.min) return 0;

  const step = (Math.log(minSize / range.min) / Math.log(range.max / range.min)) * SIZE_STEPS;
  return Math.min(SIZE_STEPS, Math.max(0, Math.round(step)));
}
